/**
 * Shared RLS predicates, so a security rule is written once rather than
 * remembered three times.
 *
 * ── Why this file exists ─────────────────────────────────────────────────
 * "Is this a real signed-in user?" was written by hand in three collections and
 * got the sentinel wrong in all three. The value `auth.uid()` returns for an
 * unauthenticated request is **`'anon'`** — rebase's auth middleware scopes the
 * driver with `{ uid: "anon", roles: ["anon"] }` (packages/server/src/auth/
 * middleware.ts) — but the rules excluded `'anonymous'`, which is a different
 * string that never appears on that path. `'anonymous'` is the value of
 * `ANONYMOUS_USER_ID` in @rebasepro/types, used by `applyAuthContext` to coerce
 * a blank uid; it is not what an anonymous HTTP caller carries.
 *
 * So `auth.uid() IS NOT NULL AND auth.uid() <> 'anonymous'` was true for every
 * signed-out visitor on the internet. Measured, not deduced: with a temporary
 * `DEFAULT auth.uid()` on a spare column, a tokenless POST recorded `'anon'`.
 * Before the fix a tokenless `POST /api/data/companies` returned 201, and a
 * tokenless `POST /api/data/company-members` claimed ownership of a company that
 * had no members — the privilege boundary `claim_unclaimed_company` exists to
 * hold.
 *
 * Commit d27b7a13 moved these rules *from* `'anon'` *to* `'anonymous'`. That is
 * the direction that broke them.
 *
 * ── The rule ─────────────────────────────────────────────────────────────
 * Exclude both spellings and NULL. `public.talents`' `require_real_user` guard
 * already did exactly this, which is why candidate PII stayed protected while
 * the other three were open — it is the shape to copy, not the exception.
 */

/**
 * True only for a request carrying a real authenticated user.
 *
 * Covers every value that is not a signed-in person: `NULL` (the trusted server
 * context), `'anon'` (what an unauthenticated HTTP request actually carries),
 * and `'anonymous'` (the blank-uid coercion sentinel).
 */
export const IS_REAL_USER =
    "auth.uid() IS NOT NULL AND auth.uid() NOT IN ('anon', 'anonymous')";
